<template>
  <div id="order-spin-off-form" class="mt-5">
    <v-row>
      <v-col cols="12" md="12" class="mb-0 pb-0">
        <p>{{ $t('views.orders.orderModal.spinOff.spinOffTitle') }}</p>
      </v-col>
      <v-col cols="12" md="12" class="mt-0 pt-0">
        <PositionSearch
          ref="positionSearch"
          v-model="myOrder.spinOff.spinOffBuyPosition"
          :currency="position.currency"
          :type="positionTypeToSpinOff"
          :disabled="loading || editing || createNewSpinOffBuyPosition"
        />
        <div class="order-modal-create-new-position mt-2">
          <a
            v-if="!editing"
            class="primary--text"
            @click="createNewSpinOffBuyPosition = !createNewSpinOffBuyPosition"
          >
            {{
              !createNewSpinOffBuyPosition
                ? $t(`views.orders.orderModal.createNewPosition.activate`)
                : $t(`views.orders.orderModal.createNewPosition.deactivate`)
            }}
          </a>
        </div>
      </v-col>
    </v-row>
    <template v-if="createNewSpinOffBuyPosition">
      <PositionForm
        :position.sync="myOrder.spinOff.spinOffBuyPosition"
        :loading="loading"
        :editing="editing"
        :positionTypesFilter="positionTypesFilter"
        :positionCurrenciesFilter="[position.currency]"
      />
    </template>

    <v-row class="position-number-row">
      <v-col cols="12" md="12" class="mb-0 mt-2 pb-2">
        <p class="mb-0">
          {{ $t(`views.orders.orderModal.spinOff.spinOffDescriptionTitle`) }}
        </p>
        <p class="mb-0 mt-3 order-spin-off-form-text">
          {{ $t(`views.orders.orderModal.spinOff.spinOffDescriptionText`) }}
        </p>
      </v-col>
      <v-col cols="12" md="5" class="mt-0 pt-0">
        <div class="items-flex align-end">
          <v-text-field
            v-model="myOrder.spinOff.spinOffBuyPositionNumber"
            type="number"
            :label="$t(`views.orders.orderModal.spinOff.positionNumber.label`)"
            :disabled="loading"
            hide-details
          />
          <span class="ml-2">
            <InfoIcon
              :size="17"
              :text="$t(`views.orders.orderModal.spinOff.positionNumber.info`)"
            />
          </span>
        </div>
      </v-col>
      <v-col cols="12" md="7" class="mt-0 pt-0">
        <span>({{ $t(`views.orders.orderModal.spinOff.rate`) }} {{ rate ? rate : ' - ' }})</span>
      </v-col>
      <v-col v-if="showSpinOffAlert" cols="12" md="12">
        <v-alert
          :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : 'softInfo'"
          icon="mdi-information"
          class="pa-2"
        >
          {{ $t(`views.orders.orderModal.spinOff.spinOffDescriptionAlert`) }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="12" class="mb-0 mt-2 pb-2">
        <p class="mb-0">
          {{ $t(`views.orders.orderModal.spinOff.buyPositionAllocation.description`) }}
        </p>
        <p class="mb-0 mt-3 order-spin-off-form-text">
          {{ $t(`views.orders.orderModal.spinOff.buyPositionAllocation.descriptionText`) }}
        </p>
      </v-col>
      <v-col cols="12" md="6" class="items-flex">
        <v-text-field
          v-model="myOrder.spinOff.spinOffBuyPositionAllocation"
          type="number"
          :label="$t(`views.orders.orderModal.spinOff.buyPositionAllocation.labelBuy`)"
          :disabled="loading"
          hide-details
          dense
          min="1"
          max="100"
          outlined
        />
        <div class="mt-1 ml-2">
          <span>%</span>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="items-flex">
        <v-text-field
          v-model="spinOffPositionAllocation"
          type="number"
          :label="$t(`views.orders.orderModal.spinOff.buyPositionAllocation.label`)"
          disabled
          dense
          outlined
          hide-details
        />
        <div class="mt-1 ml-2">
          <span>%</span>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { get } from 'lodash'
import { mapState } from 'vuex'
import PositionSearch from '@/views/components/common/PositionSearch'
import InfoIcon from '@/views/components/common/InfoIcon'
import PositionForm from '@/views/components/common/positions/PositionForm'
import CurrencyInput from '@/views/components/common/CurrencyInput'

export default {
  components: { PositionSearch, InfoIcon, PositionForm, CurrencyInput },
  props: {
    myOrder: {
      type: Object,
      required: true,
    },
    position: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      createNewSpinOffBuyPosition: false,
    }
  },
  computed: {
    ...mapState(['user']),
    positionTypeToSpinOff() {
      return this.position.type
    },
    positionTypesFilter() {
      return [this.position.type]
    },
    spinOffPositionAllocation() {
      const result = 100 - get(this.myOrder, 'spinOff.spinOffBuyPositionAllocation', 0)
      if (result > 100 || result < 0) return 0
      return result
    },
    rate() {
      const orderPositionNumber = parseFloat(get(this.myOrder, 'positionNumber', null))
      const spinOffBuyPositionNumber = parseFloat(
        get(this.myOrder, 'spinOff.spinOffBuyPositionNumber', null)
      )
      if (orderPositionNumber && spinOffBuyPositionNumber)
        return `1:${(Math.round(orderPositionNumber / spinOffBuyPositionNumber) * 100) / 100}`

      return null
    },
    showSpinOffAlert() {
      const spinOffBuyPositionNumber = get(this.myOrder, 'spinOff.spinOffBuyPositionNumber', 0)
      return spinOffBuyPositionNumber % 1 !== 0
    },
  },
  watch: {
    async createNewSpinOffBuyPosition() {
      if (this.createNewSpinOffBuyPosition) {
        this.$set(this.myOrder.spinOff, 'spinOffBuyPosition', {})
      } else {
        this.$set(this.myOrder.spinOff, 'spinOffBuyPosition', null)
      }
    },
  },
}
</script>

<style lang="scss">
#order-spin-off-form {
  .position-number-row {
    align-items: flex-end;
  }
  .order-spin-off-form-text {
    font-size: 14px;
  }
}
</style>
